import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MessageThread } from "./types";
import { createLogger } from "@/services/utils/loggingUtils";

const logger = createLogger('fetchThreads');

// Fetch all message threads for the current user
export const fetchMessageThreads = async (): Promise<MessageThread[]> => {
  try {
    // Get current user
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      toast.error("You must be logged in to view messages");
      return [];
    }

    const userId = sessionData.session.user.id;
    
    // Get distinct users the current user has exchanged messages with
    const { data: threadData, error: threadError } = await supabase.rpc(
      'get_message_threads',
      { input_user_id: userId } // Updated parameter name to match the SQL function
    );

    if (threadError) {
      logger.error("Error fetching message threads:", threadError);
      toast.error("Failed to load message threads");
      return [];
    }

    if (!threadData || threadData.length === 0) {
      return [];
    }
    
    // Sort threads to ensure those with reagent_id and those without are properly separated
    const sortedThreads = [...threadData].sort((a, b) => {
      // First by most recent message
      if (a.last_message_at > b.last_message_at) return -1;
      if (a.last_message_at < b.last_message_at) return 1;
      
      // Then group by user_id (to keep conversations with same user together)
      if (a.user_id < b.user_id) return -1;
      if (a.user_id > b.user_id) return 1;
      
      // Then by whether it's a reagent conversation or not
      if (a.reagent_id && !b.reagent_id) return -1;
      if (!a.reagent_id && b.reagent_id) return 1;
      
      return 0;
    });
    
    return sortedThreads;
  } catch (error) {
    logger.error("Exception in fetchMessageThreads:", error);
    toast.error("An unexpected error occurred");
    return [];
  }
};
